import { staleTimes, useQuery } from '@platform/api-client';
import type { RequestBody, ResponseBody, ShippingClient, ShippingOperation } from '@platform/openapi';
import { z } from 'zod';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type SenderProfileResponse = ResponseBody<ShippingOperation<'/admin/shipping/sender', 'get'>>;
export type SenderProfile = NonNullable<SenderProfileResponse['profile']>;
export type SenderBody = RequestBody<ShippingOperation<'/admin/shipping/sender', 'put'>>;

export const SENDER_LIMITS = { name: 200, phone: 32, address: 500, taxId: 40 } as const;

const required = (maxLength: number) => z.string().trim().min(1).max(maxLength);

export const senderFormSchema = z.object({
  name: required(SENDER_LIMITS.name),
  phone: required(SENDER_LIMITS.phone),
  address: required(SENDER_LIMITS.address),
  taxId: z.string().trim().max(SENDER_LIMITS.taxId),
});

export type SenderFormValues = z.infer<typeof senderFormSchema>;

export const senderFormOf = (profile: SenderProfile | null): SenderFormValues => ({
  name: profile?.name ?? '',
  phone: profile?.phone ?? '',
  address: profile?.address ?? '',
  taxId: profile?.taxId ?? '',
});

export const senderBodyOf = (values: SenderFormValues): SenderBody => ({
  name: values.name.trim(),
  phone: values.phone.trim(),
  address: values.address.trim(),
  taxId: values.taxId.trim() === '' ? null : values.taxId.trim(),
});

export const useSenderProfile = (shipping: ShippingClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.sender(tenantId),
    queryFn: () => shipping.get('/admin/shipping/sender', {}),
    staleTime: staleTimes.settings,
  });
};
